import React, { ReactNode, useCallback } from 'react';
import { Box, Stack, type SxProps, type Theme, useTheme } from '@mui/material';
import clsx from 'clsx';
import { mergeSx } from 'merge-sx';
import { useLocalStorage } from 'usehooks-ts';

import CollapseButton from '@/components/CollapseButton';
import { useDirection } from '@/hooks/useDirection';
import { fadeInKeyframe, fadeOutKeyframe } from '@/theme/animations';
import { borders } from '@/theme/constants';

type EntityLayoutProps = {
  localStorageKey: string;
  header: ReactNode;
  children: ReactNode;
  drawerContent?: ReactNode;
  sx?: SxProps<Theme>;
};

const fadeOut = `.1s ease forwards ${fadeOutKeyframe}`;

const fadeIn = `.1s ease .2s backwards ${fadeInKeyframe}`;

const EntityLayout = ({ header, children, drawerContent, localStorageKey, sx }: EntityLayoutProps) => {
  const { spacing, palette } = useTheme();
  const { start } = useDirection();
  const [isSidebarOpen, setIsSidebarOpen] = useLocalStorage(`${localStorageKey}_drawer`, true);
  const toggleSidebar = useCallback(() => setIsSidebarOpen(state => !state), [setIsSidebarOpen]);

  return (
    <Stack
      direction="column"
      component="section"
      className="EntityLayout-root"
      sx={mergeSx<Theme>(
        {
          height: '100%',
          overflowY: 'auto'
        },
        sx
      )}
    >
      <Box
        component="header"
        className="EntityLayout-header"
        sx={{
          borderBottom: borders.default,
          borderColor: 'grey.400',
          bgcolor: palette.background.default
        }}
      >
        {header}
      </Box>
      <Stack flexBasis={0} flexGrow={1} sx={{ overflowY: 'auto' }} direction="row">
        <Box component="section" className="EntityLayout-content" flexBasis={0} flexGrow={1} sx={{ overflowY: 'auto' }}>
          {children}
        </Box>

        {drawerContent && (
          <Box
            component="aside"
            flexShrink={0}
            className={clsx('EntityLayout-drawer', {
              'EntityLayout-drawer--open': isSidebarOpen
            })}
            sx={{
              position: 'relative',
              flexBasis: isSidebarOpen ? 'calc(100% / 3)' : spacing(2),
              transition: 'flex-basis .3s ease',
              bgcolor: palette.background.default,
              borderInlineStart: borders.default,
              borderColor: isSidebarOpen ? palette.background.default : 'grey.400',
              minWidth: 0
            }}
          >
            <Box
              sx={{
                height: '100%',
                overflowY: 'auto',
                animation: isSidebarOpen ? fadeIn : fadeOut
              }}
            >
              {drawerContent}
            </Box>
            <CollapseButton
              mirrored
              onClick={toggleSidebar}
              isOpen={isSidebarOpen}
              sx={{
                position: 'absolute',
                top: spacing(4),
                [start]: 0
              }}
            />
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

export default EntityLayout;
